import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { Card, Row, Col, Tooltip, OverlayTrigger } from "react-bootstrap";
import { useSelector } from "react-redux";

// Define status configuration - matching main dashboard colors
const statusConfig = {
  error: { bg: "#f5803e", color: "#fff", icon: "exclamation" },
  Error: { bg: "#f5803e", color: "#fff", icon: "exclamation" },
  invalid: { bg: "#ed5e0b", color: "#fff", icon: "exclamation" },
  "no response": { bg: "#26bcfc", color: "#fff", icon: "circle-exclamation" },
  approved: { bg: "#2c7ce6", color: "#fff", icon: "check" },
  "ready to submit": { bg: "#2c7ce6", color: "#fff", icon: "check" },
  "under progress": { bg: "#d16e34", color: "#fff", icon: "spinner" },
  rejected: { bg: "#ed5e0b", color: "#fff", icon: "times" },
  cancelled: { bg: "#269dd0", color: "#fff", icon: "times" },
  partial: { bg: "#0da9bd", color: "#fff", icon: "star-half-stroke" },
  pended: { bg: "#d16e34", color: "#fff", icon: "clock" },
  "not-required": { bg: "#197aa4", color: "#fff", icon: "clock" },
  queued: { bg: "#2a91ed", color: "#fff", icon: "list" },
  adjudication: { bg: "#b8571f", color: "#fff", icon: "gavel" },
  "validation error": {
    bg: "#ffc107", // Bootstrap warning background
    color: "#fff", // Bootstrap warning text
    icon: "exclamation-triangle",
  },
};

// Define status display order and titles
const statusTypes = [
  { key: "approved", title: "Approved" },
  { key: "under progress", title: "Under Progress" },
  { key: "partial", title: "Partial" },
  { key: "pended", title: "Pended" },
  { key: "queued", title: "Queued" },
  { key: "rejected", title: "Rejected" },
  { key: "error", title: "Error" },
  { key: "no response", title: "No Response" },
  { key: "cancelled", title: "Cancelled" },
  { key: "ready to submit", title: "Ready to Submit" },
  { key: "invalid", title: "Invalid" },
  { key: "validation error", title: "Validation Error" },
  { key: "not-required", title: "Not Required" },
];

const formatSAR = (value) => {
  const num = typeof value === "number" ? value : parseFloat(value);
  if (isNaN(num) || num === 0) return "0";
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + "M";
  } else if (num >= 1000) {
    return (num / 1000).toFixed(0) + "K";
  } else {
    return num.toFixed(0);
  }
};

const InsurancePerformanceDashboard = () => {
  const { claimDashboardData } = useSelector((state) => state.claimDashboard);
  const claim_payor = claimDashboardData?.claim_payor || [];
  const insuranceData = claim_payor.map((item) => {
    const total = item.tot_status.total || 0;
    const approvedCount = item.tot_status.approved || 0;
    const rejectedCount = item.tot_status.rejected || 0;
    const partialCount = item.tot_status["Partially Approved"] || 0;
    const pendedCount = item.tot_status.pended || 0;
    const queuedCount = item.tot_status.queue || 0;
    const errorCount = item.tot_status.Error || 0;
    const noResponseCount = item.tot_status["No Response"] || 0;
    const cancelledCount = item.tot_status.Cancelled || 0;
    const validCount = item.tot_status?.["Ready to Submit"] || 0;
    const invalidCount = item.tot_status.invalid || 0;
    const validationErrorCount = item.tot_status["validation error"] || 0;
    const notRequiredCount = item.tot_status["not-required"] || 0;
    return {
      name: item.payor_name,
      grossSar: formatSAR(item.total_grossamount),
      discount: formatSAR(item.total_discamnt),
      vat: formatSAR(item.total_vatamount),
      patientShare: formatSAR(item.total_patshreamount),
      payerShare: formatSAR(item.total_payshreamount),
      totalClaims: total.toString(),
      approvalRate: total
        ? ((approvedCount / total) * 100).toFixed(1) + "%"
        : "0%",
      approvedSAR: formatSAR(item.total_approveamount),
      rejectionRate: total
        ? ((rejectedCount / total) * 100).toFixed(1) + "%"
        : "0%",
      rejectedSAR: formatSAR(item.total_rejectionamount),
      statusCounts: {
        approved: approvedCount.toString(),
        rejected: rejectedCount.toString(),
        partial: partialCount.toString(),
        pended: pendedCount.toString(),
        queued: queuedCount.toString(),
        error: errorCount.toString(),
        "no response": noResponseCount.toString(),
        cancelled: cancelledCount.toString(),
        "ready to submit": validCount.toString(),
        invalid: invalidCount.toString(),
        "validation error": validationErrorCount.toString(),
        "not-required": notRequiredCount.toString(),
      },
    };
  });

  // Function to render tooltip with correct background and text color
  const renderTooltip = (status, config) => (
    <Tooltip
      id={`tooltip-${status}`}
      style={{
        "--bs-tooltip-bg": config.bg || "#222",
        "--bs-tooltip-color": config.color || "#fff",
      }}
      className="rounded"
    >
      {status}
    </Tooltip>
  );

  const numItems = insuranceData.length;
  const colWidth =
    numItems === 1 ? 12 : numItems === 2 ? 6 : numItems === 3 ? 4 : 3;
  console.log({ statusTypes });
  return (
    <>
      {insuranceData.map((insurance, idx) => (
        <Col xs={12} md={colWidth} key={idx}>
          <Card
            style={{ borderLeft: "3px solid #0d6efd" }}
            className="h-100 shadow-sm"
          >
            <Card.Body className="p-3">
              <div className="d-flex align-items-center justify-content-between">
                <Card.Title className="fs-10 mb-3">{insurance.name}</Card.Title>
                <div className="d-flex flex-column">
                  <span className="fs-10 text-center text-900 fw-bold">
                    {insurance.totalClaims}
                  </span>
                  <span className="fs-11 fw-semibold text-center">
                    Total Claim
                  </span>
                </div>
              </div>

              <div className="d-flex flex-wrap mt-3 gap-2 mb-3">
                {statusTypes.map((statusItem) => {
                  const count = insurance.statusCounts[statusItem.key] || "0";
                  const configKey =
                    statusItem.key === "pending"
                      ? "under progress"
                      : statusItem.key;
                  const config =
                    statusConfig[configKey] || statusConfig.rejected;

                  return (
                    <OverlayTrigger
                      key={statusItem.key}
                      placement="top"
                      overlay={renderTooltip(statusItem.title, config)}
                    >
                      <div
                        className="badge rounded-pill d-flex align-items-center gap-1 fs-11"
                        style={{
                          backgroundColor: config.bg,
                          color: config.color,
                          padding: "0.25em 0.6em",
                          cursor: "pointer",
                        }}
                      >
                        <FontAwesomeIcon icon={config.icon} size="xs" />
                        {count}
                      </div>
                    </OverlayTrigger>
                  );
                })}
              </div>

              {/* First row: 5 data points */}
              <Row className="text-center mt-3">
                <Col xs={2}>
                  <div className="fw-bold fs-10 text-primary">
                    {insurance.grossSar}
                  </div>
                  <div className="text-muted fs-11">Gross SAR</div>
                </Col>
                <Col xs={2}>
                  <div className="fw-bold fs-10 text-primary">
                    {insurance.discount}
                  </div>
                  <div className="text-muted fs-11">Disc. Amt.</div>
                </Col>
                <Col xs={2}>
                  <div className="fw-bold fs-10 text-primary">
                    {insurance.vat}
                  </div>
                  <div className="text-muted fs-11">VAT SAR</div>
                </Col>
                <Col xs={3}>
                  <div className="fw-bold fs-10 text-success">
                    {insurance.patientShare}
                  </div>
                  <div className="text-muted fs-11">Patient Share</div>
                </Col>
                <Col xs={3}>
                  <div className="fw-bold fs-10 text-success">
                    {insurance.payerShare}
                  </div>
                  <div className="text-muted fs-11">Payer Share</div>
                </Col>
              </Row>

              <hr className="my-2" />

              {/* Second row: 4 data points */}
              <Row className="text-center">
                <Col xs={3}>
                  <div className="fw-bold fs-10 text-success">
                    {insurance.approvedSAR}
                  </div>
                  <div className="text-muted fs-11">Approved SAR</div>
                </Col>
                <Col xs={3}>
                  <div className="fw-bold fs-10 text-success">
                    {insurance.approvalRate}
                  </div>
                  <div className="text-muted fs-11">Approval Rate</div>
                </Col>
                <Col xs={3}>
                  <div className="fw-bold fs-10 text-danger">
                    {insurance.rejectionRate}
                  </div>
                  <div className="text-muted fs-11">Rejection Rate</div>
                </Col>
                <Col xs={3}>
                  <div className="fw-bold fs-10 text-danger">
                    {insurance.rejectedSAR}
                  </div>
                  <div className="text-muted fs-11">Rejected SAR</div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </>
  );
};

export default InsurancePerformanceDashboard;
